import {
  Component,
  Input,
  HostListener,
  ElementRef,
  OnInit,
  OnChanges,
  SimpleChanges,
  ViewChild,
  TemplateRef,
  Output,
  EventEmitter,
} from '@angular/core';
import { AgGridAngular } from 'ag-grid-angular';
import type { ColDef, GridOptions, Column } from 'ag-grid-community';
import {
  AllCommunityModule,
  ModuleRegistry,
  themeQuartz,
} from 'ag-grid-community';
import { TablesService } from 'src/app/core/service/table.service';
import { ToastService } from 'src/app/toast/toast.service';
import { ActiveSheetObservable } from 'src/app/core/service/observable.service';
import {
  XSSheet,
  APISheet,
  RowsMeta,
  CellClickedEvent,
} from 'src/app/layout/shell/shell.model';
import { CommonModule } from '@angular/common';
import {
  NgbDropdownModule,
  NgbModal,
  NgbTooltipModule,
} from '@ng-bootstrap/ng-bootstrap';
import { FormsModule } from '@angular/forms';
import { DocumentQaInfoComponent } from 'src/app/layout/shell/document-qa-info/document-qa-info.component';

ModuleRegistry.registerModules([AllCommunityModule]);

interface Cell {
  text?: string;
  value?: string | number;
}

interface Row {
  height?: number;
  style?: number;
  cells: { [key: number]: Cell };
}

@Component({
  selector: 'app-ag-grid-view',
  standalone: true,
  imports: [
    AgGridAngular,
    CommonModule,
    NgbDropdownModule,
    NgbTooltipModule,
    FormsModule
  ],
  templateUrl: './ag-grid-view.component.html',
  styleUrl: './ag-grid-view.component.scss',
})
export class AgGridViewComponent implements OnInit, OnChanges {
  @Input() data: any;
  @Input() showLoadingSpinners: boolean = true;
  @Input() schema: string = '';
  @Input() qaActive = false;
  @Input() qaDisabled = false;
  /** When true, shows the QA info button (Dataset tab only). */
  @Input() showQaInfo = false;
  /** When true, QA button uses batch comparison (Dataset tab: compare two dataspaces by batch ID). */
  @Input() isDatasetView = false;
  @ViewChild('deleteSheetModal') deleteSheetModal!: TemplateRef<any>;
  @ViewChild('downloadModal') downloadModal!: TemplateRef<any>;
  @ViewChild('agGrid') agGrid!: AgGridAngular;
  @ViewChild('cellPopup') cellPopupRef?: ElementRef<HTMLElement>;
  @Output("cellClickedEvent") private cellClickedEmitter : EventEmitter<CellClickedEvent> = new EventEmitter();

  cellPopupVisible = false;
  cellPopupStyle: { [key: string]: string } = {};
  cellPopupData: {
    rowIndex: number;
    colField: string | null;
    colHeader: string | null;
    value: any;
    meta?: any;
  } = {
    rowIndex: 0,
    colField: null,
    colHeader: null,
    value: null,
    meta: undefined,
  };

  isTransposeHovered = false;
  transposeStaticImage = 'assets/img/icons/page_oriantiation_static.png';
  transposeAnimatedImage = 'assets/img/icons/page_oriantiation.gif';

  theme = themeQuartz;
  activeTable: string = '';
  activeTableName: string = '';
  activeSheet?: XSSheet | APISheet;
  sheets: (XSSheet | APISheet)[] = [];
  newSheetLoading = false;
  selectedSheetToDelete: string = '';
  private isGridEmpty = false;
  private actualDataRowCount = 0;
  private schemaExamples: { [key: string]: string } = {};
  private apiRowsMeta: Array<RowsMeta> = [];
  private clickTimer: any;
  private suppressNextCellClick = false;
  showTechnicalMeta = false;
  isPopupAtBottom = false;

  noRowsTemplate =
    '<div class="table-no-rows d-flex justify-content-center align-items-center h-100">The table is currently empty. Upload a file and structure it to get started.</div>';

  rowData: any[] = [];
  colDefs: ColDef[] = [];
  defaultColDef: ColDef = {
    flex: 0,
    minWidth: 100,
    editable: false,
    resizable: true,
    sortable: true,
  };

  gridOptions: GridOptions = {
    suppressHorizontalScroll: false,
    domLayout: 'normal',
    enableCellTextSelection: false,
    rowSelection: 'single',
    suppressRowTransform: true,
    pagination: false,
    rowHeight: 48,
    headerHeight: 48,
    suppressPaginationPanel: true,
    suppressScrollOnNewData: false,
    alwaysShowVerticalScroll: true,
    alwaysShowHorizontalScroll: true,
    suppressColumnVirtualisation: true,
  };

  downloadLoading = {
    xlsx: false,
    csv: false,
  };

  private isTransposed = false;
  private originalColDefs: ColDef[] = [];
  private originalRowData: any[] = [];
  private originalHeaderHeight = 48;

  get shouldShowActionButtons(): boolean {
    return !!(this.sheets && this.sheets.length > 0 && this.activeSheet && this.rowData && this.rowData.length > 0);
  }

  getSheetName(sheet: XSSheet | APISheet): string {
    return 'name' in sheet ? sheet.name : sheet.sheet_name;
  }

  constructor(
    private tableService: TablesService,
    private toastService: ToastService,
    private activeSheetObservable: ActiveSheetObservable,
    public modalService: NgbModal
  ) {}

  ngOnInit() {
    this.initializeData();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['data'] && !changes['data'].firstChange) {
      this.initializeData();
    }

    if (changes['schema'] && !changes['schema'].firstChange) {
      this.parseSchemaExamples();
    }

    if (
      changes['showLoadingSpinners'] &&
      !changes['showLoadingSpinners'].firstChange
    ) {
      this.refreshGridCells();
    }
  }

  private initializeData() {
    if (this.data) {
      this.activeTable = this.data.tableId;
      this.activeTableName = this.data.tableName;

      if (this.data.APISheets && Array.isArray(this.data.APISheets) && this.data.APISheets.length > 0) {
        this.sheets = this.data.APISheets as APISheet[];
      } else if (this.data.XSheets && Array.isArray(this.data.XSheets)) {
        this.sheets = this.data.XSheets as XSSheet[];
      } else if (Array.isArray(this.data.sheets)) {
        this.sheets = this.data.sheets as XSSheet[];
      } else if (this.data.sheets) {
        const sheetsArray = Object.entries(this.data.sheets)
          .filter(([key]) => !isNaN(Number(key)))
          .map(([_, value]) => value as XSSheet);
        this.sheets = sheetsArray;
      } else {
        this.sheets = [];
      }

      this.parseSchemaExamples();

      if (this.data.sheet) {
        const sheetAsAny = this.data.sheet as any;
        if (Array.isArray(sheetAsAny.rows) && sheetAsAny.rows.length > 0) {
          this.activeSheet = this.data.sheet;
          if (this.isAPISheet(this.data.sheet)) {
            this.transformAPISheetDataToGrid(this.data.sheet as APISheet);
          } else {
            this.transformSheetDataToGrid(this.data.sheet as XSSheet);
          }
        } else {
          this.loadSheetData(this.data.sheet);
        }
      } else if (this.sheets.length > 0) {
        const sheetAsAny = this.sheets[0] as any;
        if (Array.isArray(sheetAsAny.rows) && sheetAsAny.rows.length > 0) {
          this.activeSheet = this.sheets[0];
          if (this.isAPISheet(this.sheets[0])) {
            this.transformAPISheetDataToGrid(this.sheets[0] as APISheet);
          } else {
            this.transformSheetDataToGrid(this.sheets[0] as XSSheet);
          }
        } else {
          this.loadSheetData(this.sheets[0], true);
        }
      } else {
        this.clearGridData();
      }
    } else {
      this.clearGridData();
    }
  }

  private refreshGridCells() {
    if (this.agGrid?.api && this.colDefs.length > 0) {
      // Force a complete re-render by recreating column definitions
      // This ensures AG Grid picks up the latest loading state
      const updatedColDefs = this.colDefs.map((colDef) => ({
        ...colDef,
        cellRenderer: this.loadingCellRenderer,
      }));

      this.agGrid.api.setGridOption('columnDefs', updatedColDefs);
      this.colDefs = updatedColDefs;
    }
  }

  async switchSheet(sheet: XSSheet | APISheet) {
    if (!sheet) return;

    if (sheet.id !== this.activeSheet?.id) {
      try {
        this.cellPopupVisible = false;
        this.showTechnicalMeta = false;

        const sheetAsAny = sheet as any;
        if (Array.isArray(sheetAsAny.rows) && sheetAsAny.rows.length > 0) {
          this.activeSheet = sheet;
          if (this.isAPISheet(sheet)) {
            this.transformAPISheetDataToGrid(sheet as APISheet);
          } else {
            this.transformSheetDataToGrid(sheet as XSSheet);
          }
          sheet.loaded = true;
        } else {
          const data = await this.tableService.getSheetData(sheet.id, true);
          if (data) {
            this.activeSheet = data.sheet;
            console.log('Switching sheet to:', this.activeSheet?.id);
            await this.loadSheetData(this.activeSheet);
            sheet.loaded = true;
          }
        }
      } catch (error) {
        console.error('Error switching sheet:', error);
        this.toastService.showError('Error', 'Failed to switch sheet');
      }
    } else {
      sheet.showDropDown = !sheet.showDropDown;
    }

    this.sheets.forEach((s) => {
      if (s.id !== sheet.id) {
        s.loaded = false;
        s.showDropDown = false;
      }
    });
  }

  editSheetName(sheet: XSSheet | APISheet) {
    const sheetName = 'name' in sheet ? sheet.name : sheet.sheet_name;
    sheet.editedSheetName = sheetName;
    sheet.isSheetNameEdit = true;
  }

  stopEditingSheet(sheet: XSSheet | APISheet) {
    if (!sheet.editedSheetName) return;

    const updateData = 'name' in sheet 
      ? { name: sheet.editedSheetName }
      : { sheet_name: sheet.editedSheetName };

    const updateMethod = 'name' in sheet
      ? this.tableService.updateSheets(this.activeTable, sheet.id, updateData)
      : this.tableService.updateAPISheetTable(this.activeTable, sheet.id, updateData);

    updateMethod.subscribe((res: any) => {
      if ('name' in sheet) {
        sheet.name = sheet.editedSheetName!;
      } else {
        sheet.sheet_name = sheet.editedSheetName!;
      }
      sheet.isSheetNameEdit = false;
    });
  }

  createNewSheet() {
    if (!this.activeTable) {
      this.toastService.showError('Error', 'No active table selected');
      return;
    }

    this.newSheetLoading = true;
    this.tableService.createSheet(this.activeTable).subscribe({
      next: async (res: any) => {
        if (res._createSheet?.sheetId) {
          const sheetData = await this.tableService.getSheetData(
            res._createSheet.sheetId,
            true
          );
          if (sheetData?.sheet) {
            let newSheet: XSSheet | APISheet;
            if ('rows' in sheetData.sheet && typeof sheetData.sheet.rows === 'object' && !Array.isArray(sheetData.sheet.rows)) {
              newSheet = {
                ...sheetData.sheet,
                rows: {},
                cols: { len: 0 },
                merges: [],
                styles: [],
                freeze: [],
              } as XSSheet;
            } else {
              newSheet = {
                id: sheetData.sheet.id,
                sheet_name: 'name' in sheetData.sheet ? sheetData.sheet.name : 'New Sheet',
                rows: [],
                rowsMeta: [],
                mode: { type: 'add' },
                createdAt: new Date().toISOString(),
                lastEditedAt: new Date().toISOString(),
                loaded: false,
              } as APISheet;
            }
            this.sheets = [...this.sheets, newSheet];
            this.activeSheet = newSheet;

            this.clearGridData();

            this.toastService.show('info', 'New sheet created successfully', {
              classname:
                'bg-white text-dark position-fixed bottom-0 end-0 mb-7 me-3',
            });
          }
        }
      },
      error: (error) => {
        console.error('Error creating new sheet:', error);
        this.toastService.showError('Error', 'Failed to create new sheet');
      },
      complete: () => {
        this.newSheetLoading = false;
      },
    });
  }

  private async loadSheetData(sheet: XSSheet | APISheet, silent: boolean = false) {
    try {
      this.cellPopupVisible = false;
      this.showTechnicalMeta = false;

      const sheetData = await this.tableService.getSheetData(sheet.id, true);
      if (sheetData?.sheet) {
        this.activeSheet = sheetData.sheet;
        if (!silent) {
          this.activeSheetObservable.setActiveSheet(sheet.id, this.activeTable);
        }

        const isEmpty = this.isSheetEmpty(sheetData.sheet);
        if (isEmpty) {
          this.clearGridData();
          return;
        }

        if (this.isAPISheet(sheetData.sheet)) {
          this.transformAPISheetDataToGrid(sheetData.sheet);
        } else {
          this.transformSheetDataToGrid(sheetData.sheet);
        }
      }
    } catch (error) {
      console.error('Error loading sheet data:', error);
    }
  }

  private isAPISheet(sheet: any): sheet is APISheet {
    return sheet && Array.isArray(sheet.rows) && 'sheet_name' in sheet;
  }

  private isSheetEmpty(sheet: any): boolean {
    if (this.isAPISheet(sheet)) {
      return !sheet.rows || sheet.rows.length === 0;
    } else {
      return !sheet.rows || Object.keys(sheet.rows).length === 0;
    }
  }

  private transformAPISheetDataToGrid(sheet: APISheet) {
    console.log('Transforming APISheet data to grid:', sheet);
    
    const sheetAny: any = sheet as any;
    this.apiRowsMeta = Array.isArray(sheetAny.rowsMeta) ? sheetAny.rowsMeta : [];

    this.colDefs = [];
    this.rowData = [];

    if (!sheet.rows || sheet.rows.length === 0) {
      this.clearGridData();
      return;
    }

    const columnNames: string[] = [];

    const addColumnName = (columnName: string) => {
      if (
        columnName &&
        !columnNames.includes(columnName)
      ) {
        columnNames.push(columnName);
      }
    };
    sheet.rows.forEach((row) => {
      if (row && typeof row === 'object') {
        Object.keys(row).forEach((columnName) => addColumnName(columnName));
      }
    });

    if (columnNames.length === 0) {
      columnNames.push('Column 1');
    }

    // Ensure 'Filename' column is always first
    const filenameIndex = columnNames.indexOf('Filename');
    if (filenameIndex > -1) {
      columnNames.splice(filenameIndex, 1);
      columnNames.unshift('Filename');
    }

    this.colDefs = columnNames.map((columnName) => ({
      field: columnName,
      colId: columnName,
      headerName: columnName,
      valueGetter: (params) => params.data?.[columnName],
      cellRenderer: this.loadingCellRenderer,
      headerClass: 'bold-header',
      ...this.defaultColDef,
    }));

    const dataRows = sheet.rows;
    this.actualDataRowCount = dataRows.length;

    let processedRowData = dataRows.map((row, index) => {
      const rowData: any = { id: index.toString() };
      columnNames.forEach((columnName) => {
        rowData[columnName] = row[columnName] || '';
      });
      return rowData;
    });

    const currentRowCount = this.actualDataRowCount;
    if (currentRowCount < 30) {
      const emptyRowsNeeded = 30 - currentRowCount;
      const emptyRows = Array(emptyRowsNeeded)
        .fill(null)
        .map((_, index) => {
          const rowData: any = { id: (currentRowCount + index).toString() };
          columnNames.forEach((columnName) => {
            rowData[columnName] = '';
          });
          return rowData;
        });
      processedRowData = [...processedRowData, ...emptyRows];
    }

    const hasActualData = processedRowData.some((row) =>
      Object.keys(row).some(
        (key) => key !== 'id' && row[key] && row[key].toString().trim() !== ''
      )
    );
    this.isGridEmpty = !hasActualData;

    if (dataRows.length < 5) {
      this.originalColDefs = this.colDefs;
      this.originalRowData = processedRowData;
      this.originalHeaderHeight = this.gridOptions.headerHeight || 48;
      this.isTransposed = true;

      const columnMetaKeys = this.colDefs.map((col, index) =>
        col.colId ?? col.field ?? col.headerName ?? `col${index}`
      );

      const newColDefs: ColDef[] = [
        {
          field: 'header',
          headerName: ' ',
          width: 150,
          suppressMovable: true,
          cellStyle: {
            backgroundColor: '#f8f9fa',
            fontWeight: 'bold',
            borderRight: '1px solid #dee2e6',
          },
        },
        ...processedRowData.map((_, index) => ({
          field: `col${index}`,
          colId: columnMetaKeys[index] ?? `col${index}`,
          headerName: ' ',
          cellRenderer: this.loadingCellRenderer,
          ...this.defaultColDef,
        })),
      ];

      const currentHeaders = columnMetaKeys;
      const currentData = processedRowData.map((row) =>
        this.colDefs.map((col) => row[col.field || ''])
      );

      const newRowData = currentHeaders.map((header, rowIndex) => {
        const rowData: any = { header: header };
        currentData.forEach((row, colIndex) => {
          rowData[`col${colIndex}`] = row[rowIndex];
        });
        return rowData;
      });

      this.colDefs = newColDefs;
      this.rowData = newRowData;
      this.gridOptions = {
        ...this.gridOptions,
        headerHeight: 24,
      };
    } else {
      this.isTransposed = false;
      this.colDefs = this.colDefs;
      this.rowData = processedRowData;
      this.gridOptions = {
        ...this.gridOptions,
        headerHeight: 48,
      };
    }

    if (this.agGrid?.api) {
      this.agGrid.api.setGridOption(
        'headerHeight',
        this.isTransposed ? 24 : 48
      );
      this.agGrid.api.setGridOption('columnDefs', this.colDefs);
      this.agGrid.api.setGridOption('rowData', this.rowData);
      setTimeout(() => {
        const allColumnIds: string[] = [];
        this.agGrid.api.getColumns()?.forEach((column) => {
          allColumnIds.push(column.getId());
        });
        this.agGrid.api.autoSizeColumns(allColumnIds, false);

        const gridContainer = this.agGrid.api.getGridOption('domLayout') === 'normal'
          ? this.agGrid.api.getHorizontalPixelRange()?.right || 0
          : 0;
        
        const actualGridWidth = gridContainer || 600; // Use reasonable default width
           
        const maxWidth = actualGridWidth * 0.3;

        const columnStates = this.agGrid.api.getColumnState();
        const updatedColumnStates = columnStates.map((state) => ({
          ...state,
          width: state.width && state.width > maxWidth ? maxWidth : state.width,
        }));
        this.agGrid.api.applyColumnState({ state: updatedColumnStates });
      });
    }
  }

  private transformSheetDataToGrid(sheet: XSSheet) {
    const rows = Object.entries(sheet.rows)
      .filter(([key, value]) => key !== 'len' && typeof value !== 'number')
      .sort((a, b) => parseInt(a[0]) - parseInt(b[0]));

    this.colDefs = [];
    this.rowData = [];

    if (rows.length === 0) {
      this.clearGridData();
      return;
    }

    const headerRowEntry = rows[0];
    const dataRowEntries = rows.slice(1);
    this.actualDataRowCount = dataRowEntries.length;

    const firstRow = headerRowEntry[1] as Row;
    const headerRow = rows.length > 0 ? firstRow : null;

    const columns = new Map<string, string>();
    if (headerRow && headerRow.cells) {
      Object.entries(headerRow.cells).forEach(([colKey, cell]) => {
        columns.set(
          colKey,
          cell.text || cell.value?.toString() || `Column ${colKey}`
        );
      });
    }

    if (columns.size === 0) {
      columns.set('col0', 'Column 1');
    }

    const standardColDefs = Array.from(columns.entries())
      .map(([colKey, headerText]) => ({
        field: colKey,
        colId: headerText,
        headerName: headerText,
        cellRenderer: this.loadingCellRenderer,
        headerClass: 'bold-header',
        ...this.defaultColDef,
      }))
      .sort((a, b) => {
        if (a.headerName === 'Filename') return -1;
        if (b.headerName === 'Filename') return 1;
        return 0;
      });

    let standardRowData = dataRowEntries.map(([rowKey, row]) => {
      const rowData: any = { id: rowKey };
      if ((row as Row).cells) {
        Object.entries((row as Row).cells).forEach(([colKey, cell]) => {
          rowData[colKey] = cell.text || cell.value;
        });
      }
      standardColDefs.forEach((col) => {
        if (col.field && !(col.field in rowData)) {
          rowData[col.field] = '';
        }
      });
      return rowData;
    });

    const currentRowCount = this.actualDataRowCount;
    if (currentRowCount < 30) {
      const emptyRowsNeeded = 30 - currentRowCount;
      const emptyRows = Array(emptyRowsNeeded)
        .fill(null)
        .map((_, index) => {
          const rowData: any = { id: (currentRowCount + index).toString() };
          standardColDefs.forEach((col) => {
            if (col.field) {
              rowData[col.field] = '';
            }
          });
          return rowData;
        });
      standardRowData = [...standardRowData, ...emptyRows];
    }

    const hasActualData = standardRowData.some((row) =>
      Object.keys(row).some(
        (key) => key !== 'id' && row[key] && row[key].toString().trim() !== ''
      )
    );
    this.isGridEmpty = !hasActualData;

    if (rows.length < 5) {
      this.originalColDefs = standardColDefs;
      this.originalRowData = standardRowData;
      this.originalHeaderHeight = this.gridOptions.headerHeight || 48;
      this.isTransposed = true;

      const columnMetaKeys = standardColDefs.map((col, index) =>
        col.colId ?? col.field ?? col.headerName ?? `col${index}`
      );

      const newColDefs: ColDef[] = [
        {
          field: 'header',
          headerName: ' ',
          width: 150,
          suppressMovable: true,
          cellStyle: {
            backgroundColor: '#f8f9fa',
            fontWeight: 'bold',
            borderRight: '1px solid #dee2e6',
          },
        },
        ...standardRowData.map((_, index) => ({
          field: `col${index}`,
          colId: columnMetaKeys[index] ?? `col${index}`,
          headerName: ' ',
          cellRenderer: this.loadingCellRenderer,
          ...this.defaultColDef,
        })),
      ];

      const currentHeaders = columnMetaKeys;
      const currentData = standardRowData.map((row) =>
        standardColDefs.map((col) => row[col.field || ''])
      );

      const newRowData = currentHeaders.map((header, rowIndex) => {
        const rowData: any = {
          header: header,
        };
        currentData.forEach((row, colIndex) => {
          rowData[`col${colIndex}`] = row[rowIndex];
        });
        return rowData;
      });

      this.colDefs = newColDefs;
      this.rowData = newRowData;
      this.gridOptions = {
        ...this.gridOptions,
        headerHeight: 24,
      };
    } else {
      this.isTransposed = false;
      this.colDefs = standardColDefs;
      this.rowData = standardRowData;
      this.gridOptions = {
        ...this.gridOptions,
        headerHeight: 48,
      };
    }

    if (this.agGrid?.api) {
      this.agGrid.api.setGridOption(
        'headerHeight',
        this.isTransposed ? 24 : 48
      );
      this.agGrid.api.setGridOption('columnDefs', this.colDefs);
      this.agGrid.api.setGridOption('rowData', this.rowData);
      setTimeout(() => {
        const allColumnIds: string[] = [];
        this.agGrid.api.getColumns()?.forEach((column) => {
          allColumnIds.push(column.getId());
        });
        this.agGrid.api.autoSizeColumns(allColumnIds, false);

        const gridContainer = this.agGrid.api.getGridOption('domLayout') === 'normal'
          ? this.agGrid.api.getHorizontalPixelRange()?.right || 0
          : 0;
        
        const actualGridWidth = gridContainer || 600; // Use reasonable default width
           
        const maxWidth = actualGridWidth * 0.3;

        const columnStates = this.agGrid.api.getColumnState();
        const updatedColumnStates = columnStates.map((state) => ({
          ...state,
          width: state.width && state.width > maxWidth ? maxWidth : state.width,
        }));
        this.agGrid.api.applyColumnState({ state: updatedColumnStates });
      });
    }
  }

  onCellValueChanged(event: any) {
    if (!this.activeTable || !this.activeSheet?.id) return;
    
    const data = {
      value: event.newValue,
      oldValue: event.oldValue
    };

    if (this.isAPISheet(this.activeSheet)) {
      // Use new APISheet update method
      this.tableService.updateAPITableCell(
        this.activeTable,
        this.activeSheet.id,
        event.rowIndex,
        event.colDef.field,
        data
      ).subscribe({
        error: (error) => {
          console.error('Error updating cell:', error);
          this.toastService.showError('Error', 'Failed to update cell');
        }
      });
    } else {
      // Use legacy XSheet update method
      this.tableService.updateTableCell(
        this.activeTable,
        this.activeSheet.id,
        event.rowIndex + 1,
        event.colDef.field,
        data
      ).subscribe({
        error: (error) => {
          console.error('Error updating cell:', error);
          this.toastService.showError('Error', 'Failed to update cell');
        }
      });
    }
  }

  duplicateSheet(sheet: XSSheet | APISheet) {
    this.tableService
      .duplicateSheet(this.activeTable, sheet)
      .subscribe((res: any) => {
        if (res.data?.sheets) {
          this.sheets = res.data.sheets;
          this.toastService.show('info', 'Sheet is duplicated successfully.', {
            classname:
              'bg-white text-dark position-fixed bottom-0 end-0 mb-7 me-3',
          });
        }
      });
  }

  openDeleteModal(sheetId: string) {
    this.selectedSheetToDelete = sheetId;
    this.modalService.open(this.deleteSheetModal, { centered: true });
  }

  deleteSheet(sheetId: string) {
    this.tableService
      .deleteSheet(this.activeTable, sheetId)
      .subscribe((res: any) => {
        const index = this.sheets.findIndex((s) => s.id === sheetId);
        if (index > -1) {
          this.sheets.splice(index, 1);
          if (this.sheets.length > 0) {
            this.loadSheetData(this.sheets[0]);
          } else {
            this.clearGridData();
          }
        }
        this.toastService.show('info', 'Sheet is deleted.', {
          classname:
            'bg-white text-dark position-fixed bottom-0 end-0 mb-7 me-3',
        });
      });
  }

  cleanGrid() {
    this.clearGridData();
    this.toastService.show('info', 'Grid data cleared successfully', {
      classname: 'bg-white text-dark position-fixed bottom-0 end-0 mb-7 me-3',
    });
  }

  private clearGridData() {
    this.actualDataRowCount = 0;
    this.colDefs = [];
    this.rowData = [];
    this.isGridEmpty = true;
    this.activeSheet = undefined;
    this.apiRowsMeta = [];
    this.cellPopupVisible = false;
    
    if (this.agGrid?.api) {
      this.agGrid.api.setGridOption('columnDefs', []);
      this.agGrid.api.setGridOption('rowData', []);
      
      setTimeout(() => {
        if (this.agGrid?.api) {
          this.agGrid.api.showNoRowsOverlay();
        }
      }, 0);
    }
  }

  onCellDoubleClicked(event: any) {
    if (this.clickTimer) {
      clearTimeout(this.clickTimer);
      this.clickTimer = null;
    }

    try {
      if (
        event.value === null || 
        event.value === undefined || 
        event.value.toString().trim() === '' || 
        event.colDef.field === 'header'
      ) {
        return;
      }

      // Calculate popup width and position
      const gridElement = document.querySelector('.container-fluid');
      const containerRect = gridElement ? gridElement.getBoundingClientRect() : null;
      
      let rowIndex = 0, columnKey = '';
      if (this.isTransposed) {
        rowIndex = parseInt(event.colDef.field.replace('col', ''));
        columnKey = event.data.header;
      } else {
        rowIndex = event.rowIndex;
        columnKey = event.colDef.field;
      }
      const cellMeta = this.apiRowsMeta[rowIndex][columnKey];
      
      // Show full content popup
      this.cellPopupData = {
        rowIndex: rowIndex,
        colField: event.colDef.field,
        colHeader: event.colDef.headerName,
        value: event.value,
        meta: cellMeta
      };

      const nativeEvent = event.event;
      let target = nativeEvent.target;
      while (target && target.classList && !target.classList.contains('ag-cell')) {
        target = target.parentElement;
      }
      const rect = target ? target.getBoundingClientRect() : null;
      const isBottomHalf = rect ? rect.top > window.innerHeight / 2 : false;

      // Calculate width from cell's left border to container's right border
      let targetWidth = 0;
      if (rect && containerRect) {
        targetWidth = containerRect.right - rect.left + 2;
      } else {
        targetWidth = (gridElement?.clientWidth || window.innerWidth) * 0.5;
      }

      this.cellPopupStyle = {
        position: 'fixed',
        top: isBottomHalf 
          ? (rect ? `${rect.bottom + 2}px` : `${nativeEvent.clientY - 10}px`)
          : (rect ? `${rect.top - 2}px` : `${nativeEvent.clientY + 10}px`),
        left: rect ? `${rect.left - 2}px` : `${nativeEvent.clientX - 10}px`,
        transform: isBottomHalf ? 'translate(0, -100%)' : 'translate(0, 0)',
        width: `${targetWidth}px`,
        maxWidth: 'none',
        zIndex: '1000'
      };
      this.cellPopupVisible = true;
      this.isPopupAtBottom = isBottomHalf;
    } catch (error) {
      console.error('Error getting cell meta:', error);
    }
  }

  onCellClicked(event: any) {
    if (this.suppressNextCellClick) {
      this.suppressNextCellClick = false;
      return;
    }
    if (this.cellPopupVisible) {
      return;
    }
    if (event.colDef.field === 'header') {
      return;
    }
    if (this.clickTimer) {
      clearTimeout(this.clickTimer);
    }

    this.clickTimer = setTimeout(() => {
      try {
        let rowIndex = 0, columnKey = '';
        if (this.isTransposed) {
          rowIndex = parseInt(event.colDef.field.replace('col', ''));
          columnKey = event.data.header;
        } else {
          rowIndex = event.rowIndex;
          columnKey = event.colDef.field;
        }
        const cellMeta = this.apiRowsMeta[rowIndex][columnKey];
        // emit event to parent component with cell meta
        this.cellClickedEmitter.emit({
          rowIndex: rowIndex,
          columnName: columnKey,
          cellMeta: cellMeta,
          tableId: this.activeTable,
          sheetId: this.activeSheet?.id || '',
          value: event.value || null,
        });
      } catch (error) {
        console.error('Error getting cell meta:', error);
      }
      this.clickTimer = null;
    }, 250);
  }

  onBodyScroll(event: any) {
    if (this.cellPopupVisible) {
      this.closeCellPopup();
    }
  }

  closeCellPopup() {
    this.cellPopupVisible = false;
    (this as any).showTechnicalMeta = false;
  }

  @HostListener('document:mousedown', ['$event'])
  onDocumentMouseDown(event: MouseEvent) {
    if (!this.cellPopupVisible) {
      return;
    }
    const target = event.target as Node | null;
    if (this.cellPopupRef?.nativeElement && target) {
      if (this.cellPopupRef.nativeElement.contains(target)) {
        return;
      }
    }
    // Prevent cell click handling for the same interaction.
    this.suppressNextCellClick = true;
    this.closeCellPopup();
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeyDown(event: KeyboardEvent) {
    if (event.key !== 'Escape') {
      return;
    }
    if (!this.cellPopupVisible) {
      return;
    }
    this.closeCellPopup();
  }

  transposeGrid() {
    if (!this.agGrid?.api || !this.rowData.length || !this.colDefs.length) {
      this.toastService.showError('Error', 'No data to transpose');
      return;
    }

    if (!this.isTransposed) {
      this.originalColDefs = [...this.colDefs];
      this.originalRowData = [...this.rowData];
      this.originalHeaderHeight = this.gridOptions.headerHeight || 48;

      const columnMetaKeys = this.colDefs.map((col, index) =>
        col.colId ?? col.field ?? col.headerName ?? `col${index}`
      );
      const currentHeaders = columnMetaKeys;
      const currentData = this.rowData.map((row) =>
        this.colDefs.map((col) => row[col.field || ''])
      );

      const newColDefs: ColDef[] = [
        {
          field: 'header',
          headerName: ' ',
          width: 150,
          suppressMovable: true,
          cellStyle: {
            backgroundColor: '#f8f9fa',
            fontWeight: 'bold',
            borderRight: '1px solid #dee2e6',
          },
        },
        ...currentData.map((_, index) => ({
          field: `col${index}`,
          colId: columnMetaKeys[index] ?? `col${index}`,
          headerName: ' ',
          cellRenderer: this.loadingCellRenderer,
          ...this.defaultColDef,
        })),
      ];

      const newRowData = currentHeaders.map((header, rowIndex) => {
        const rowData: any = {
          header: header,
        };
        currentData.forEach((row, colIndex) => {
          rowData[`col${colIndex}`] = row[rowIndex];
        });
        return rowData;
      });

      this.gridOptions = {
        ...this.gridOptions,
        headerHeight: 24,
      };
      this.colDefs = newColDefs;
      this.rowData = newRowData;
      this.agGrid.api.setGridOption('headerHeight', 24);
      this.agGrid.api.setGridOption('columnDefs', this.colDefs);
      this.agGrid.api.setGridOption('rowData', this.rowData);
    } else {
      this.gridOptions = {
        ...this.gridOptions,
        headerHeight: this.originalHeaderHeight,
      };
      this.colDefs = this.originalColDefs;
      this.rowData = this.originalRowData;
      this.agGrid.api.setGridOption('headerHeight', this.originalHeaderHeight);
      this.agGrid.api.setGridOption('columnDefs', this.colDefs);
      this.agGrid.api.setGridOption('rowData', this.rowData);
    }

    this.isTransposed = !this.isTransposed;

    setTimeout(() => {
      const allColumnIds: string[] = [];
      this.agGrid.api.getColumns()?.forEach((column) => {
        allColumnIds.push(column.getId());
      });
      this.agGrid.api.autoSizeColumns(allColumnIds, false);

      const gridElement =
        this.agGrid.api.getGridOption('domLayout') === 'normal'
          ? this.agGrid.api.getHorizontalPixelRange()?.right || 0
          : window.innerWidth;
      const maxWidth = gridElement * 0.3;

      const columnStates = this.agGrid.api.getColumnState();
      const updatedColumnStates = columnStates.map((state) => ({
        ...state,
        width: state.width && state.width > maxWidth ? maxWidth : state.width,
      }));
      this.agGrid.api.applyColumnState({ state: updatedColumnStates });
    });
  }

  openDownloadModal() {
    if (!this.activeTable) {
      this.toastService.showError('Error', 'No active table selected');
      return;
    }
    this.modalService.open(this.downloadModal, { centered: true });
  }

  async downloadXlsx() {
    if (!this.activeTable) {
      this.toastService.showError('Error', 'No active table selected');
      return;
    }

    this.downloadLoading.xlsx = true;
    this.tableService.downloadXlsx(this.activeTable).subscribe({
      next: (blob: any) => {
        this.tableService.triggerDownload(blob, `${this.activeTableName}.xlsx`);
        this.downloadLoading.xlsx = false;
        this.modalService.dismissAll();
      },
      error: (error) => {
        console.error('Error downloading XLSX:', error);
        this.downloadLoading.xlsx = false;
        this.toastService.showError('Error', 'Failed to download XLSX');
      },
    });
  }

  async downloadCsv() {
    if (!this.activeSheet?.rows) {
      this.toastService.showError('Error', 'No sheet data to export');
      return;
    }

    this.downloadLoading.csv = true;
    try {
      const csv = this.buildCsvFromSheetRows(this.activeSheet);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const baseName = (this.data.fileName ?? this.data.tableName ?? 'download').toString().replace(/\.[^.]*$/, '');
      const sanitized = baseName.replace(/[/\\?%*:|"<>]/g, '_').trim() || 'download';
      const filename = `${sanitized}.csv`;
      this.tableService.triggerDownload(blob, filename);
      this.modalService.dismissAll();
    } catch (error) {
      console.error('Error building CSV:', error);
      this.toastService.showError('Error', 'Failed to download CSV');
    } finally {
      this.downloadLoading.csv = false;
    }
  }

  /**
   * Builds a CSV string from sheet.rows.
   * - rows[0].cells (XSSheet) or first row keys (APISheet) = column headers
   * - Remaining rows = data. Handles both array and object-style rows.
   */
  private buildCsvFromSheetRows(sheet: any): string {
    const rawRows = sheet.rows;
    const rowsArray: Row[] = Array.isArray(rawRows)
      ? rawRows
      : Object.entries(rawRows || {})
          .filter(([k, v]) => k !== 'len' && typeof v !== 'number')
          .sort((a, b) => parseInt(String(a[0]), 10) - parseInt(String(b[0]), 10))
          .map(([, v]) => v as Row);

    if (rowsArray.length === 0) {
      return '';
    }

    const first = rowsArray[0];
    const hasCells = first && typeof first === 'object' && 'cells' in first && first.cells;

    if (hasCells) {
      const headerRow = first as Row;
      const colKeys = Object.entries(headerRow.cells || {})
        .sort((a, b) => parseInt(String(a[0]), 10) - parseInt(String(b[0]), 10))
        .map(([k]) => k);
      const headers = colKeys.map((k) => this.getCellDisplayValue(headerRow.cells![Number(k)]));
      const dataRows = rowsArray.slice(1);
      const lines = [this.csvEscapeRow(headers)];
      for (const row of dataRows) {
        const r = row as Row;
        const values = colKeys.map((k) => this.getCellDisplayValue(r.cells?.[Number(k)]));
        lines.push(this.csvEscapeRow(values));
      }
      return lines.join('\r\n');
    } else {
      const rowObjects = rowsArray as unknown as Record<string, string | number>[];
      const columnNames = this.collectColumnNames(rowObjects);
      const lines = [this.csvEscapeRow(columnNames)];
      for (const row of rowObjects) {
        const values = columnNames.map((col) => String(row[col] ?? ''));
        lines.push(this.csvEscapeRow(values));
      }
      return lines.join('\r\n');
    }
  }

  private getCellDisplayValue(cell: Cell | undefined): string {
    if (!cell) return '';
    const v = cell.text ?? cell.value;
    return v != null ? String(v) : '';
  }

  private collectColumnNames(rows: Record<string, string | number>[]): string[] {
    const set = new Set<string>();
    for (const row of rows) {
      if (row && typeof row === 'object') {
        Object.keys(row).forEach((k) => set.add(k));
      }
    }
    let names = Array.from(set);
    const filenameIdx = names.indexOf('Filename');
    if (filenameIdx > -1) {
      names.splice(filenameIdx, 1);
      names.unshift('Filename');
    }
    return names.length ? names : ['Column 1'];
  }

  private csvEscapeRow(values: string[]): string {
    return values
      .map((v) => {
        const s = String(v ?? '');
        if (/[,\r\n"]/.test(s)) {
          return '"' + s.replace(/"/g, '""') + '"';
        }
        return s;
      })
      .join(',');
  }

  private loadingCellRenderer = (params: any) => {
    return this.renderLoadingCell(params);
  };

  private renderLoadingCell(params: any): string {
    const shouldShowSpinner = this.shouldShowLoadingSpinner(params);

    if (shouldShowSpinner) {
      let fieldName = params.colDef.field;
      let example = '';

      if (this.isTransposed && fieldName !== 'header') {
        const headerValue = params.data?.header;
        if (headerValue && this.schemaExamples[headerValue]) {
          example = this.schemaExamples[headerValue];
        }
      } else if (!this.isTransposed && this.schemaExamples[fieldName]) {
        example = this.schemaExamples[fieldName];
      }

      let exampleText = '';
      if (example) {
        exampleText = `<span style="margin-left: 8px; color: #6c757d; font-style: italic; font-size: 0.875rem;">${example}</span>`;
      }

      return `
        <div style="display: flex; align-items: center; height: 48px; width: 100%;">
          <div class="spinner-border spinner-border-sm" role="status" style="width: 1rem; height: 1rem; color: #673ab7; flex-shrink: 0;">
            <span class="visually-hidden">Loading...</span>
          </div>
          ${exampleText}
        </div>
      `;
    }

    return params.value || '';
  }

  private shouldShowLoadingSpinner(params: any): boolean {
    const currentTotalRows = this.rowData ? this.rowData.length : 0;

    if (!this.showLoadingSpinners) return false;

    if (this.isTransposed) {
      const targetSpinnerColumnField = 'col' + this.actualDataRowCount;
      return params.colDef.field === targetSpinnerColumnField;
    } else {
      if (this.actualDataRowCount < currentTotalRows) {
        return params.node.rowIndex === this.actualDataRowCount;
      } else {
        return params.node.rowIndex === 0;
      }
    }
  }

  private parseSchemaExamples() {
    this.schemaExamples = {};

    if (!this.schema) {
      return;
    }

    try {
      const parsedSchema = JSON.parse(this.schema);

      if (parsedSchema.$defs) {
        Object.values(parsedSchema.$defs).forEach((def: any) => {
          if (def.properties) {
            Object.entries(def.properties).forEach(
              ([fieldName, fieldDef]: [string, any]) => {
                if (fieldDef.examples && fieldDef.examples.length > 0) {
                  this.schemaExamples[fieldName] = fieldDef.examples[0];
                }
              }
            );
          }
        });
      }

      if (parsedSchema.properties && parsedSchema.properties.sheets) {
        const sheets = parsedSchema.properties.sheets;
        if (sheets.items && Array.isArray(sheets.items)) {
          sheets.items.forEach((sheetDef: any) => {
            if (
              sheetDef.properties &&
              sheetDef.properties.rows &&
              sheetDef.properties.rows.items
            ) {
              const rowItems = sheetDef.properties.rows.items;
              if (rowItems.properties) {
                Object.entries(rowItems.properties).forEach(
                  ([fieldName, fieldDef]: [string, any]) => {
                    if (fieldDef.examples && fieldDef.examples.length > 0) {
                      this.schemaExamples[fieldName] = fieldDef.examples[0];
                    }
                  }
                );
              }
            }
          });
        }
      }

      console.log('Parsed schema examples:', this.schemaExamples);
    } catch (error) {
      console.error('Error parsing schema:', error);
    }
  }
}
